// Reads the white-label demo page's configuration out of the URL query, so the page can be
// dropped into a reseller's iframe already showing their branding:
//
//   #/profile-customization/demo?name=Acme%20Mobile&accentColor=EA580C&logoUrl=https://...
//
// Every value is optional. A missing one falls back to UNCONFIGURED_BRANDING - what a reseller
// who has configured nothing actually gets - rather than to an invented brand, which would read
// as "your settings did not apply" to anyone embedding the page. The editable settings header is
// hidden unless `settings` is passed.
//
// These params are attacker-controllable whenever the page is embedded, so each one is
// validated rather than trusted: a colour has to be hex, and a URL has to carry a scheme that
// is safe for the element it lands in.

import {ResellerInformation} from "@/interfaces/entities/CustomerProfile";
import {DemoScenario, DEMO_SCENARIOS, PLACEHOLDER_LOGO_URL} from "@/demo/whitelabelDemoData";

export type PreviewPage = "profile" | "install";

export interface DemoConfig {
    branding: ResellerInformation;
    preview: PreviewPage;
    scenario: DemoScenario;
    settingsVisible: boolean;
}

// Vue Router's `$route.query` shape: a repeated key arrives as an array, a valueless key as null.
export type DemoQuery = Record<string, string | (string | null)[] | null | undefined>;

// The default for any param that is absent: what a reseller who has configured nothing sees,
// which is the branded pages' own fallbacks - no logo, no brand name, the default accent.
export const UNCONFIGURED_BRANDING: ResellerInformation = {
    name: "",
    logoUrl: "",
    accentColor: "#0B7A73",
    faqUrl: ""
};

// Only reachable from the settings header's "Sample brand" button, for testing by hand -
// deliberately nothing like the default teal, so it is obvious at a glance which parts of the
// design the accent colour drives. Never a fallback for a missing param.
export const SAMPLE_BRANDING: ResellerInformation = {
    name: "Acme Mobile",
    logoUrl: PLACEHOLDER_LOGO_URL,
    accentColor: "#EA580C",
    faqUrl: "https://support.example.com"
};

const PREVIEW_PAGES: Array<PreviewPage> = ["profile", "install"];
const HEX_PARAM = /^#?[0-9a-fA-F]{6}$/;
// SVG served to an <img> cannot run script, so inline images are safe to accept here.
// The separator is `;` when a parameter follows (`;base64,`) and `,` when the data is raw,
// which is the form PLACEHOLDER_LOGO_URL itself takes - both have to match.
const IMAGE_DATA_URI = /^data:image\/(png|jpeg|jpg|gif|webp|avif|svg\+xml)[;,]/i;
const SAFE_URL_SCHEMES = ["http:", "https:"];
const MAX_NAME_LENGTH = 64;
const TRUTHY = ["true", "1", "yes", "on"];
const FALSY = ["false", "0", "no", "off"];

/**
 * The single value for `key`, trimmed. `null` means the key was absent or given with no value
 * at all; an empty string means it was present but deliberately blank, which callers treat as
 * "clear this field" rather than "use the default".
 */
function readParam(query: DemoQuery, key: string): string | null {
    const raw = query[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value === null || value === undefined) return null;
    return String(value).trim();
}

function readColor(query: DemoQuery, key: string, fallback: string): string {
    const raw = readParam(query, key);
    // A colour cannot be blank - the pages always need something to paint with.
    if (raw === null || !HEX_PARAM.test(raw)) return fallback;
    const hex = raw.startsWith("#") ? raw.slice(1) : raw;
    return `#${hex.toUpperCase()}`;
}

function hasSafeScheme(raw: string): boolean {
    try {
        // Resolved against the current page so a relative path stays usable.
        return SAFE_URL_SCHEMES.includes(new URL(raw, window.location.href).protocol);
    } catch (e) {
        return false;
    }
}

/** For an `<a href>`, where a `javascript:` URL would actually execute. */
function readLinkUrl(query: DemoQuery, key: string, fallback: string): string {
    const raw = readParam(query, key);
    if (raw === null) return fallback;
    if (raw === "") return "";
    return hasSafeScheme(raw) ? raw : fallback;
}

/** For an `<img src>`: the same schemes, plus inline image data URIs. */
function readImageUrl(query: DemoQuery, key: string, fallback: string): string {
    const raw = readParam(query, key);
    if (raw === null) return fallback;
    if (raw === "") return "";
    if (IMAGE_DATA_URI.test(raw)) return raw;
    return hasSafeScheme(raw) ? raw : fallback;
}

function readText(query: DemoQuery, key: string, fallback: string, maxLength: number): string {
    const raw = readParam(query, key);
    if (raw === null) return fallback;
    return raw.slice(0, maxLength);
}

function readFlag(query: DemoQuery, key: string, fallback: boolean): boolean {
    if (!(key in query)) return fallback;
    const raw = readParam(query, key);
    // A bare `?settings` reads as opting in.
    if (raw === null || raw === "") return true;
    const value = raw.toLowerCase();
    if (TRUTHY.indexOf(value) !== -1) return true;
    if (FALSY.indexOf(value) !== -1) return false;
    return fallback;
}

function readOption<T extends string>(query: DemoQuery, key: string, allowed: Array<T>, fallback: T): T {
    const raw = readParam(query, key);
    if (raw === null) return fallback;
    const match = allowed.filter((option: T) => option.toLowerCase() === raw.toLowerCase());
    return match.length > 0 ? match[0] : fallback;
}

export function parseDemoConfig(query: DemoQuery): DemoConfig {
    return {
        branding: {
            name: readText(query, "name", UNCONFIGURED_BRANDING.name, MAX_NAME_LENGTH),
            logoUrl: readImageUrl(query, "logoUrl", UNCONFIGURED_BRANDING.logoUrl),
            accentColor: readColor(query, "accentColor", UNCONFIGURED_BRANDING.accentColor),
            faqUrl: readLinkUrl(query, "faqUrl", UNCONFIGURED_BRANDING.faqUrl)
        },
        preview: readOption<PreviewPage>(query, "preview", PREVIEW_PAGES, "profile"),
        scenario: readOption<DemoScenario>(query, "scenario", DEMO_SCENARIOS, "healthy"),
        settingsVisible: readFlag(query, "settings", false)
    };
}
